import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { C } from '../lib/theme';
import { styles } from '../lib/styles';
import { apiFetch } from '../api/client';
import { Dialog } from './Dialog';

// #540 — per-user selectable queue columns.
//
// Three staff queues (Vetting Cases, Onboarding, Intake) had run out of
// horizontal room: Cases already carried ten columns, and the issue needed two
// more on each. Rather than drop columns or stack a second line under
// Registrant, each analyst chooses what their own queues show.
//
// SERVER-side, in user_preferences.queue_columns, and NOT in web storage. That
// is the acceptance criterion: an analyst who signs in from a different browser
// — the wall display, a colleague's machine, a rebuilt laptop — must find their
// own layout, and a device-local store would give them somebody else's
// defaults. It is the same split lib/prefs.ts describes: theme and density are
// per device on purpose, a column layout is per person.
//
// Worded as "web storage" deliberately. The gate that enforces the CLAUDE.md
// invariant is a plain grep that does not distinguish code from a comment, and
// its pattern matches the API name followed by a dot — which a sentence ending
// in that name supplies. lib/prefs.ts is the one file exempted, and naming the
// API here in prose was enough to turn the gate red. Read that workflow before
// rewording this paragraph.
//
// WHAT IS STORED is the visible SET, not an order. Render order always follows
// each queue's canonical column order below, so a column switched off and back
// on returns to where it belongs instead of landing at the end, and a release
// that inserts a column puts it in the right place for everybody at once.
//
// The vocabulary is duplicated in apps/api/src/routes/me.ts rather than shared,
// because apps/spa and apps/api share no package (the same reason
// api/preferences.ts duplicates the notification events). The API is the
// authority: its PUT body schema spells each queue's keys as a JSON Schema
// `enum`, so a key that drifts out of step here is a visible 400 and never a
// stored preference that matches no column.

export const QUEUE_IDS = ['cases', 'onboarding', 'intake'] as const;
export type QueueId = (typeof QUEUE_IDS)[number];

export interface QueueColumn<K extends string = string> {
  key: K;
  label: string;
}

// ---- The canonical column order, per queue --------------------------------
//
// The two RCD columns sit next to the registrant rather than at the end, which
// is the whole point of the issue: an analyst scanning for a SHAFT-risky
// display name should meet it beside the company it belongs to, not eleven
// columns to the right of it. Their position here does not affect the default
// view, which does not include them.

export const CASES_COLUMNS = [
  { key: 'case', label: 'Case' },
  { key: 'registrant', label: 'Registrant' },
  { key: 'displayName', label: 'Caller display name' },
  { key: 'callReason', label: 'Call reason' },
  { key: 'customer', label: 'Customer' },
  { key: 'status', label: 'Status' },
  // #766 — the case progress tracker's stage and first open item. OFF by
  // default, like the RCD columns: nobody's existing layout changes.
  { key: 'stage', label: 'Stage / next step' },
  { key: 'oa', label: 'Authorizer' },
  { key: 'eid', label: 'EID' },
  { key: 'created', label: 'Created' },
  { key: 'sla', label: 'SLA' },
  { key: 'notification', label: 'Notification' },
  { key: 'settlement', label: 'Settlement' },
] as const satisfies readonly QueueColumn[];
export type CasesColumnKey = (typeof CASES_COLUMNS)[number]['key'];

export const ONBOARDING_COLUMNS = [
  { key: 'case', label: 'Case' },
  { key: 'registrant', label: 'Registrant' },
  { key: 'displayName', label: 'Caller display name' },
  { key: 'callReason', label: 'Call reason' },
  { key: 'oa', label: 'Authorizer' },
  { key: 'fein', label: 'FEIN' },
  { key: 'jurisdiction', label: 'Jurisdiction' },
  { key: 'onboardingStatus', label: 'Onboarding Status' },
  { key: 'stage', label: 'Stage / next step' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'vettingStatus', label: 'Vetting Status' },
] as const satisfies readonly QueueColumn[];
export type OnboardingColumnKey = (typeof ONBOARDING_COLUMNS)[number]['key'];

export const INTAKE_COLUMNS = [
  { key: 'submission', label: 'Submission' },
  { key: 'customer', label: 'Customer' },
  { key: 'legalName', label: 'Legal Name' },
  { key: 'displayName', label: 'Caller display name' },
  { key: 'callReason', label: 'Call reason' },
  { key: 'orgType', label: 'Org Type' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'status', label: 'Status' },
] as const satisfies readonly QueueColumn[];
export type IntakeColumnKey = (typeof INTAKE_COLUMNS)[number]['key'];

export const QUEUE_COLUMNS: Record<QueueId, readonly QueueColumn[]> = {
  cases: CASES_COLUMNS,
  onboarding: ONBOARDING_COLUMNS,
  intake: INTAKE_COLUMNS,
};

/** What each queue is called in the picker's own heading. */
export const QUEUE_LABELS: Record<QueueId, string> = {
  cases: 'Vetting Cases',
  onboarding: 'Onboarding Applications',
  intake: 'Intake Queue',
};

// ---- The defaults ---------------------------------------------------------
//
// Spelled out key by key rather than sliced off the lists above, so a reviewer
// can hold each one against the header row of the page it belongs to. These
// ARE today's header rows, exactly: an analyst who never opens the picker sees
// what they saw before this issue and nothing else. The two RCD columns are
// available and OFF.
export const DEFAULT_QUEUE_COLUMNS: Record<QueueId, readonly string[]> = {
  cases: [
    'case', 'registrant', 'customer', 'status', 'oa', 'eid',
    'created', 'sla', 'notification', 'settlement',
  ],
  onboarding: [
    'case', 'registrant', 'oa', 'fein', 'jurisdiction',
    'onboardingStatus', 'submitted', 'vettingStatus',
  ],
  intake: ['submission', 'customer', 'legalName', 'orgType', 'submitted', 'status'],
};

export type QueueColumnPrefs = Record<QueueId, readonly string[]>;

function defaults(): QueueColumnPrefs {
  return {
    cases: DEFAULT_QUEUE_COLUMNS.cases,
    onboarding: DEFAULT_QUEUE_COLUMNS.onboarding,
    intake: DEFAULT_QUEUE_COLUMNS.intake,
  };
}

/**
 * Reduce whatever came back for one queue to keys that name a real column, in
 * canonical order.
 *
 * Two independent reasons this is not just a pass-through, both of which the
 * API cannot solve on its own:
 *
 *   * A key stored by an older release whose column this release has retired.
 *     The API refuses an unknown key on the way IN, but a row written before
 *     the retirement is already there, and rendering it would ask a page for a
 *     cell it has no case for.
 *   * A set that survives the filter empty. A zero-column table is not a
 *     preference anybody expressed, and it also breaks the empty-state
 *     colSpan, so it falls back to the defaults rather than rendering nothing.
 */
export function coerceQueueColumns(queue: QueueId, raw: unknown): readonly string[] {
  if (!Array.isArray(raw)) return DEFAULT_QUEUE_COLUMNS[queue];
  const wanted = new Set(raw.filter((k): k is string => typeof k === 'string'));
  const kept = QUEUE_COLUMNS[queue].filter(col => wanted.has(col.key)).map(col => col.key);
  return kept.length > 0 ? kept : DEFAULT_QUEUE_COLUMNS[queue];
}

export function coerceAllQueueColumns(raw: unknown): QueueColumnPrefs {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    cases: coerceQueueColumns('cases', r.cases),
    onboarding: coerceQueueColumns('onboarding', r.onboarding),
    intake: coerceQueueColumns('intake', r.intake),
  };
}

// ---- The store ------------------------------------------------------------
//
// A module-level store read through useSyncExternalStore, the shape lib/prefs.ts
// already uses, rather than a context provider. Three pages and a picker share
// one copy of the answer and one fetch per session, with no provider to thread
// through App.tsx and nothing added to UIStateContext — whose handles
// (`openCase`, `openSubmission`) are page hand-offs for one navigation, not
// persisted view state.

let state: QueueColumnPrefs = defaults();
let inflight: Promise<void> | null = null;
let loaded = false;
// ---- THE PER-QUEUE RULE (#624 item 1) -------------------------------------
//
// A save writes ONE queue, and its answer is authoritative about THAT QUEUE
// ONLY — even though the response carries the full resolved map. The other two
// entries in it were read from the row as it stood when that request reached
// the database, so a save for another queue racing it makes them stale.
// Committing them is the bug this rule exists to stop: a Cases save and an
// Onboarding save overlap, the Onboarding answer lands first carrying a `cases`
// entry from a row that did not yet hold the Cases write, the store adopts it,
// and the Cases save then finds itself superseded and returns without
// correcting it. The Cases list showed the defaults for the rest of the session
// while the row held both changes.
//
// So everything below is per queue: the server-known layout, the landing order
// that decides which answer describes the row, and the latest-save number that
// decides which answer reaches the screen. A save commits, records and rolls
// back its own queue's entry and nothing else.
//
// The corollary is that the LOAD's answer is now the only thing that will ever
// put the OTHER queues' stored layouts on screen — a save answer no longer
// speaks for them — so it cannot be dropped wholesale because one queue is
// mid-save either. It applies per queue too.

/**
 * The layout the SERVER is known to hold, per queue — the rollback target for a
 * refused save, and NOT the state from just before the click (#608 item 2).
 * Those two are the same thing only once the load has landed: a checkbox
 * clicked while the first GET is still in flight snapshots the DEFAULTS, so
 * rolling back to it parks a person with a stored non-default layout on
 * defaults the server does not hold, `loaded` latches on the GET that lands
 * next, the once-per-session load never retries, and the wrong layout stands
 * until a reload.
 *
 * A save's answer supersedes the load's FOR THE QUEUE IT SAVED: the GET was
 * issued first, so its answer is older than any PUT racing it. Among saves for
 * the same queue the LAST ANSWER TO LAND wins, which is a different question
 * from the one `latestSaveSeq` settles below — that picks which answer to put
 * on SCREEN (the latest save issued for that queue), while this picks which one
 * describes the row (the latest answer received for it). An older save's late
 * reply must not overwrite a newer one's here either. A queue absent from this
 * map is one no answer has spoken for yet.
 */
const serverKnown = new Map<QueueId, readonly string[]>();
// Which answer `serverKnown` holds for each queue: absent — read as 0 — is the
// load, and every save's `seq` starts at 1, so a save always outranks it. The
// load leaves this alone and instead READS it as its own guard: a queue a save
// answer has already spoken for is one the load's older answer cannot describe.
// That per-queue guard is what the whole-map `loaded` check used to do, and it
// has to be per queue now, because a save answer for one queue is no reason to
// throw away the load's word about the other two.
const serverKnownSeq = new Map<QueueId, number>();
// Which queue every save still awaiting a response is for, and what it asked
// for, by `seq`. Three jobs: a load answering while one is outstanding is
// dropped FOR THAT QUEUE rather than committed (#608 item 3), a refused save
// rolls back to the NEWEST save for ITS OWN queue still in flight rather than
// to the stored layout (see `rollbackTarget`), and the recorded queue is what
// keeps both of those from reaching across queues.
const savesInFlight = new Map<number, { queue: QueueId; keys: readonly string[] }>();
// Monotonic across every queue, so one number orders any two saves.
let saveSeq = 0;
// The newest save issued PER QUEUE. Toggling two checkboxes on one queue in
// quick succession puts two PUTs in flight and responses can land out of
// order — so the older one's answer would roll that queue back to a layout the
// server no longer holds, and the analyst would watch the second change undo
// itself and then reappear on reload. Only the latest save's answer is adopted.
// Per queue and not one global counter, because a save for a DIFFERENT queue
// says nothing about this one: with a single counter the Cases answer in the
// interleaving described above counted as superseded by an unrelated Onboarding
// save, and returned without ever putting the row's own words on screen.
const latestSaveSeq = new Map<QueueId, number>();
const listeners = new Set<() => void>();

/** Whether any save for this queue is still awaiting its response. */
function hasSaveInFlight(queue: QueueId): boolean {
  for (const save of savesInFlight.values()) if (save.queue === queue) return true;
  return false;
}

function emit(): void {
  for (const l of listeners) l();
}

function sameKeys(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((k, i) => k === b[i]);
}

/**
 * Adopt a new map, notifying subscribers only when something actually moved.
 *
 * The common case by far is a load that answers exactly what is already on
 * screen — the defaults, for everybody who has never opened the picker — and
 * re-rendering three queues to say nothing changed is a wasted pass.
 *
 * When nothing moved, the OLD object is kept rather than swapped for an equal
 * one. `useSyncExternalStore` compares snapshot identity and re-reads it after
 * subscribing, so handing out a fresh object without notifying would make the
 * snapshot disagree with the one the render used and cost a re-render anyway —
 * which is precisely what the comparison above exists to avoid.
 */
function commit(next: QueueColumnPrefs): void {
  if (!QUEUE_IDS.some(q => !sameKeys(state[q], next[q]))) return;
  state = next;
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function getQueueColumns(): QueueColumnPrefs {
  return state;
}

export async function fetchQueueColumns(): Promise<{ queueColumns: Record<string, string[]> }> {
  return apiFetch<{ queueColumns: Record<string, string[]> }>('/me/queue-columns');
}

/**
 * Persist ONE queue's set. The rule: send only what changed.
 *
 * The PUT merges per queue and a queue the body leaves out is not written
 * (#608 item 1), so the body carries exactly the queue that moved. Sending the
 * whole map is what this fix removes: two of the three sets in `state` at any
 * moment are usually the DEFAULTS rendered for queues this person has never
 * touched, and sending those back stored them as if they had been chosen —
 * freezing today's defaults into the row and cutting that person off from
 * every default set a later release changes.
 *
 * Absence is therefore load-bearing on this side too. It is the only way the
 * SPA can say "I have no opinion about that queue", and it is what keeps
 * "never opened the picker" distinguishable from "ticked exactly the
 * defaults". Widening this back to the full map would restore the bug in full
 * with no visible symptom until the next release moves a default.
 *
 * The response still answers the FULL resolved map. The caller adopts only the
 * queue it saved out of it, because the other two entries describe the row as
 * it stood when THIS request reached the database and a save for one of them
 * racing this one makes them stale (#624 item 1).
 */
export async function saveQueueColumns(
  queueColumns: Partial<QueueColumnPrefs>,
): Promise<{ queueColumns: Record<string, string[]> }> {
  return apiFetch<{ queueColumns: Record<string, string[]> }>('/me/queue-columns', {
    method: 'PUT',
    body: JSON.stringify({ queueColumns }),
  });
}

/**
 * Load this person's stored layout once per session.
 *
 * A failure leaves the defaults in place and is not surfaced: the queue still
 * renders, in the layout everybody had before this issue, which is a working
 * page rather than an error screen over a column preference. The first paint
 * uses the defaults too, so a stored layout that differs settles in on the
 * response — the same fetch the queue's own rows are waiting for.
 */
export function ensureQueueColumnsLoaded(): Promise<void> {
  // Only a SUCCESSFUL LOAD latches. A failure leaves `loaded` false, so the
  // next queue the analyst opens tries again — deliberately: being stuck on the
  // defaults for a whole session because of one dropped request is the worse
  // outcome, and the retry costs one small GET per page mount.
  //
  // A successful SAVE used to latch it too, which cost nothing when a save
  // answer put all three queues on screen. Under the per-queue rule it does
  // not: a save speaks only for the queue it saved, so latching here after a
  // FAILED load would leave the other two queues on the defaults for the rest
  // of the session with nothing left that could ever correct them (#624).
  if (loaded) return Promise.resolve();
  inflight ??= fetchQueueColumns()
    .then((res) => {
      loaded = true;
      const fromServer = coerceAllQueueColumns(res.queueColumns);
      const next: QueueColumnPrefs = { ...state };
      for (const queue of QUEUE_IDS) {
        // A save for THIS queue completed while this load was in flight, so it
        // already holds the newer truth about it; adopting this answer would
        // undo it on screen. Per queue, not for the whole map: a save answer
        // says nothing about the queues it did not save (#624 item 1), so
        // dropping their entries here would leave them on the defaults for the
        // rest of the session — `loaded` has just latched and the
        // once-per-session GET never runs again.
        if ((serverKnownSeq.get(queue) ?? 0) > 0) continue;
        // Worth recording even when it is not worth showing: if the save now in
        // flight for this queue is refused, THIS is the layout to fall back to.
        serverKnown.set(queue, fromServer[queue]);
        // A save for this queue is still in flight, and its optimistic value is
        // on screen. That value is the newer intent, so this queue's entry is
        // DROPPED rather than deferred until the save settles: committing it
        // would revert the checkbox under the analyst for as long as the PUT
        // takes and then move it back, and deferring it would land a layout the
        // save's own answer is about to replace. Whichever way the save goes it
        // writes the right thing — its response on success, `serverKnown` above
        // on failure — so there is nothing left for the load to do here.
        if (hasSaveInFlight(queue)) continue;
        next[queue] = fromServer[queue];
      }
      commit(next);
    })
    .catch(() => { /* defaults stand; see above */ })
    .finally(() => { inflight = null; });
  return inflight;
}

/** Test seam: forget the loaded copy and go back to the defaults. */
export function resetQueueColumnsStore(): void {
  state = defaults();
  loaded = false;
  inflight = null;
  saveSeq = 0;
  serverKnown.clear();
  serverKnownSeq.clear();
  latestSaveSeq.clear();
  savesInFlight.clear();
  emit();
}

/**
 * Where to put ONE QUEUE when a save for it is refused. Only that queue moves:
 * every other queue on screen is either the row's own words or another save's
 * business, and a refused Cases save has nothing to say about Onboarding
 * (#624 item 1 — reaching across queues here is how a refused save used to
 * land the OTHER queues on a layout two answers stale).
 *
 * The rule, in order, and every candidate is scoped to `queue`:
 *
 *   1. The newest save for this queue STILL IN FLIGHT that is also newer than
 *      the last answer adopted for it, if there is one. Its change is on its
 *      way to the server and belongs on screen, and — this is the part that
 *      makes it necessary rather than merely tidy — nothing else will put it
 *      there. When it lands it is superseded (`seq !== latestSaveSeq`, the
 *      refused save having taken the last number for this queue) and takes the
 *      early return without committing, so rolling past it here leaves the
 *      screen a change behind the row for the rest of the session. It is that
 *      save's PAYLOAD rather than its answer because the answer has not
 *      arrived yet; any normalisation the server applies to it arrives with
 *      the next load or save.
 *   2. Otherwise the layout the server is known to hold for this queue —
 *      `serverKnown`, which is the load's answer, or the last accepted save
 *      for this queue.
 *   3. Otherwise this queue's defaults, for a session where nothing has
 *      answered about it yet.
 *
 * "Newer than the last answer adopted" is what keeps rule 1 from undoing rule
 * 2: a save that answered while an older one was still outstanding has already
 * put the row's own words on screen, and an older save's payload is a guess
 * that would replace them.
 *
 * What this is NOT is the state from just before the click. That agrees with
 * rule 1 while an older save is outstanding, and with rule 2 once one has
 * answered; it is wrong in the case that has neither, because a click that
 * beats the once-per-session load snapshots the DEFAULTS (#608 item 2).
 */
function rollbackTarget(queue: QueueId, failed: number): readonly string[] {
  // Starting the search at this queue's `serverKnownSeq` is rule 1's "newer
  // than the last answer adopted": a save at or below it loses to `serverKnown`
  // below.
  let newest = serverKnownSeq.get(queue) ?? 0;
  let payload: readonly string[] | null = null;
  for (const [seq, save] of savesInFlight) {
    // A save for another queue is not a candidate whatever its sequence
    // number, and must not advance `newest` either.
    if (save.queue !== queue) continue;
    // `failed` is still in the map — its own `finally` has not run yet — and
    // it is the save being rolled back, so it is not a candidate.
    if (seq === failed || seq <= newest) continue;
    newest = seq;
    payload = save.keys;
  }
  return payload ?? serverKnown.get(queue) ?? DEFAULT_QUEUE_COLUMNS[queue];
}

/**
 * Replace one queue's visible set, optimistically, and persist it.
 *
 * The local write lands first so the table redraws under the analyst's click.
 * A rejected save is rolled BACK rather than left standing: a layout that
 * looks applied and then disappears on the next sign-in is worse than one that
 * visibly refused to change, and `onError` says which happened.
 *
 * Rolling back does NOT mean going back to what was on screen when the click
 * happened; `rollbackTarget` above states the rule and the reasons.
 */
export async function setQueueColumns(
  queue: QueueId,
  keys: readonly string[],
  onError?: (message: string) => void,
): Promise<void> {
  const seq = ++saveSeq;
  const wanted = coerceQueueColumns(queue, keys);
  savesInFlight.set(seq, { queue, keys: wanted });
  latestSaveSeq.set(queue, seq);
  // The whole map goes to `commit` because the STORE holds the whole map, but
  // only this queue's entry differs from what is already on screen. The REQUEST
  // carries only the queue that moved too; see `saveQueueColumns` for why the
  // other two must not travel with it.
  commit({ ...state, [queue]: wanted });
  try {
    const res = await saveQueueColumns({ [queue]: wanted });
    // Deliberately NOT latching `loaded` here — see `ensureQueueColumnsLoaded`.
    // ONLY this queue's entry out of the full map the response carries (#624
    // item 1). The other two were read from the row as it stood when this
    // request reached the database, so a save for one of them racing this one
    // makes them stale — and this save has no business overwriting a queue it
    // did not write.
    const answered = coerceQueueColumns(queue, res.queueColumns[queue]);
    if (seq > (serverKnownSeq.get(queue) ?? 0)) {
      serverKnown.set(queue, answered);
      serverKnownSeq.set(queue, seq);
    }
    // Superseded for THIS queue, which is the only comparison that means
    // anything: a later save for a different queue does not make this answer
    // stale about `queue`, and treating it as if it did is what left the
    // overlapping-save case uncorrected.
    if (seq !== latestSaveSeq.get(queue)) return;
    commit({ ...state, [queue]: answered });
  } catch (err) {
    // Every refused save rolls back, superseded or not. There used to be a
    // latest-save guard here, from when the target was the state before
    // the click and rolling a superseded save back would have discarded the
    // newer save's change along with this one. `rollbackTarget` cannot do
    // that: a newer save still in flight IS the target, and a newer save that
    // has already answered outranks every payload. So on a superseded failure
    // the target is what is already on screen and the commit is a no-op —
    // except in the one case where it is not, which is why the guard had to
    // go: when the newer save has ALSO failed, this save's own change is the
    // only thing still on screen and nothing else is coming to take it off.
    commit({ ...state, [queue]: rollbackTarget(queue, seq) });
    onError?.(`Could not save your column choice: ${err instanceof Error ? err.message : String(err)}`);
  } finally {
    savesInFlight.delete(seq);
  }
}

/**
 * The visible columns for one queue, in canonical order, as the page renders
 * them. Kicks off the one-per-session load on first use.
 */
export function useQueueColumns(queue: QueueId): readonly QueueColumn[] {
  useEffect(() => { void ensureQueueColumnsLoaded(); }, []);
  const prefs = useSyncExternalStore(subscribe, getQueueColumns, getQueueColumns);
  const visible = new Set(prefs[queue]);
  return QUEUE_COLUMNS[queue].filter(col => visible.has(col.key));
}

// ---- The picker -----------------------------------------------------------

/**
 * "Columns" button plus the dialog behind it.
 *
 * A checkbox per available column, in canonical order, with the last remaining
 * one disabled: a table with no columns has no empty state to render and no
 * way back except this dialog, so the floor is one. The API enforces the same
 * floor (`minItems: 1`), which is what makes it a rule rather than a habit of
 * this particular button.
 */
export function QueueColumnPicker({ queue, onError }: {
  queue: QueueId;
  onError?: (message: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const prefs = useSyncExternalStore(subscribe, getQueueColumns, getQueueColumns);
  const selected = prefs[queue];
  const isLast = selected.length === 1;

  const toggle = useCallback((key: string, on: boolean) => {
    const next = on
      ? [...selected, key]
      : selected.filter(k => k !== key);
    void setQueueColumns(queue, next, onError);
  }, [queue, selected, onError]);

  return (
    <>
      <button
        type="button"
        style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
        onClick={() => setOpen(true)}
      >Columns</button>
      {open && (
        <Dialog title={`Columns — ${QUEUE_LABELS[queue]}`} onClose={() => setOpen(false)}>
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>
            Your choice, saved to your account — it follows you to any browser you sign in from.
          </div>
          <div>
            {QUEUE_COLUMNS[queue].map((col) => {
              const on = selected.includes(col.key);
              return (
                <label key={col.key} style={{ ...styles.checkItem, cursor: on && isLast ? 'not-allowed' : 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={on && isLast}
                    onChange={e => toggle(col.key, e.target.checked)}
                  />
                  {col.label}
                </label>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button
              type="button"
              style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
              // Saves the default list EXPLICITLY, which is a choice like any
              // other and not the same as having no stored preference: this
              // person then keeps these columns when a later release changes
              // the default set. Saying "track the defaults" instead would need
              // a way to erase a queue from the row, and the PUT has no token
              // for that (#608 item 1).
              onClick={() => void setQueueColumns(queue, DEFAULT_QUEUE_COLUMNS[queue], onError)}
            >Reset to default</button>
            <button
              type="button"
              style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }}
              onClick={() => setOpen(false)}
            >Done</button>
          </div>
        </Dialog>
      )}
    </>
  );
}
